import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Target, Brain, BookOpen } from 'lucide-react';
import { QuizApiService } from '@/services/QuizApiService';
import { quizSessionKeys } from '@/hooks/queries/useQuizSession';
import { TestType } from '@/models/SharedTypes';
import { toast } from 'react-hot-toast';
import { logger } from '@/utils/logger';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { isFreeTierEnabled } from '@/config/featureFlags';
import { isDailyLimitError, DailyLimitError } from '@/utils/freeTier';
import { FreeCapReached } from '@/features/quiz/components/FreeCapReached';

// Global deduplication - super simple approach
let lastInitializationTime = 0;
const DEDUP_WINDOW_MS = 2000; // 2 second window to prevent duplicates

interface QuizLoadingProps {
  // For direct quiz initialization
  testType?: TestType;
  /**
   * Backend-owned session shape (aswb-backend/src/config/sessionModes.ts):
   * 'mock' = the fixed 2026 blueprint exam (122 questions), 'practice'/'category'
   * = endless. When present, targetQuestionCount is ignored server-side — kept
   * here only for the legacy no-mode dialect and for cache-seeding below.
   *
   * #300 fix: this was previously never forwarded to initializeSession, so
   * every "Full Exam" tap fell through to the legacy numeric-count path
   * instead of the true mock policy.
   */
  sessionMode?: 'practice' | 'category' | 'mock';
  targetQuestionCount?: number;
  preferredDifficulty?: number;
  categories?: string[];
  startQuestionId?: string; // Deep-link: start on a specific question (email CTA)
  onQuizReady?: (sessionId: string) => void;
}

const QuizLoading: React.FC<QuizLoadingProps> = ({
  testType,
  sessionMode,
  targetQuestionCount,
  preferredDifficulty,
  categories,
  startQuestionId,
  onQuizReady
}) => {
  logger.debug('QuizLoading', 'Component rendered with props', { testType, sessionMode, targetQuestionCount, preferredDifficulty, categories, startQuestionId });
  
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams();
  const { currentUser, isLoading } = useEnhancedAuth();
  const [currentStep, setCurrentStep] = useState('Initializing...');
  const [error, setError] = useState<string | null>(null);
  // Metered free tier (flagged): a capped user starting via /start → /quiz/loading
  // hits the daily limit at initializeSession — that's an expected conversion
  // moment, not an init failure, so it renders the in-flow paywall below.
  const [capError, setCapError] = useState<DailyLimitError | null>(null);
  const initializationStarted = useRef(false);
  const quizApiService = QuizApiService.getInstance();
  const queryClient = useQueryClient();

  // Use refs to capture latest values without causing re-renders
  const onQuizReadyRef = useRef(onQuizReady);
  const quizApiServiceRef = useRef(quizApiService);
  
  // Update refs when props change
  useEffect(() => {
    onQuizReadyRef.current = onQuizReady;
    quizApiServiceRef.current = quizApiService;
  });

  // Handle quiz initialization after authentication is complete
  useEffect(() => {
    const now = Date.now();

    logger.debug('QuizLoading', 'Initialization check', {
      testType,
      currentUser: !!currentUser,
      initializationStarted: initializationStarted.current,
      urlSessionId,
      isLoading
    });

    if (isLoading) {
      logger.debug('QuizLoading', 'Auth still loading, waiting...');
      return;
    }

    if (!currentUser) {
      const redirectTarget = urlSessionId ? `/quiz/${urlSessionId}` : '/quiz/loading';
      const encoded = encodeURIComponent(redirectTarget);
      logger.warn('QuizLoading', 'No authenticated user detected, redirecting to sign-in');
      navigate(`/signin?from=${encoded}`, { replace: true });
      return;
    }
    
    // IMMEDIATE global check - if ANY initialization happened recently, skip
    if (lastInitializationTime && (now - lastInitializationTime) < DEDUP_WINDOW_MS) {
      logger.warn('QuizLoading', 'Recent initialization detected, skipping duplicate call');
      return;
    }
    
    // IMMEDIATE local check
    if (initializationStarted.current) {
      logger.warn('QuizLoading', 'Local initialization already started, skipping');
      return;
    }
    
    // Set BOTH flags immediately
    lastInitializationTime = now;
    initializationStarted.current = true;
    logger.success('QuizLoading', 'Starting quiz initialization', { timestamp: now });
    
    const initializeQuiz = async () => {
      try {
        // If we have a sessionId in URL, this is for continuing an existing quiz
        if (urlSessionId) {
          setCurrentStep('Loading your quiz session...');
          navigate(`/quiz/${urlSessionId}`, { replace: true });
          return;
        }

        // If we have props, this is for starting a new quiz
        if (testType) {
          setCurrentStep('Selecting questions based on your progress...');
          const response = await quizApiServiceRef.current.initializeSession({
            testType,
            ...(sessionMode && { sessionMode }), // #300: Full Exam must reach the backend as 'mock'
            targetQuestionCount,
            preferredDifficulty,
            categories,
            ...(startQuestionId && { startQuestionId }) // Deep-link to a specific question
          });

          // Seed the react-query cache from the init response so QuizSession
          // renders the first question immediately, instead of firing a SECOND
          // full loadSession round-trip for the session we just created.
          // (Mirrors useInitializeSession — keep the two mappings in sync.)
          queryClient.setQueryData(quizSessionKeys.session(response.sessionId), {
            sessionId: response.sessionId,
            testType,
            currentQuestion: response.firstQuestion,
            currentQuestionNote: null,
            adaptiveState: response.adaptiveState,
            sessionProgress: {
              questionsAnswered: 0,
              totalQuestions: response.sessionConfig.targetQuestions,
              currentScore: 0,
              timeElapsed: 0,
              wrongAnswers: []
            },
            sessionConfig: response.sessionConfig,
            sessionAskedQuestions: response.firstQuestion?.id ? [response.firstQuestion.id] : [],
            isComplete: false,
            sessionComplete: false,
            waitingForBackend: false,
            nextQuestion: null,
            nextQuestionNote: null,
            categories, // Focus categories → honest "{Category} · Focus Session" title
          });

          // Track analytics with actual session ID
          const quizIdNumber = response.sessionId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
          logAnalyticsEvent(ANALYTICS_EVENTS.QUIZ_STARTED, {
            quiz_id: quizIdNumber,
            quiz_type: testType,
            category: categories?.[0] // Track first category if it's a category quiz
          });

          setCurrentStep('Ready! Starting your quiz...');

          // Clear timestamp since we're done (allow new initializations)
          lastInitializationTime = 0;

          if (onQuizReadyRef.current) {
            onQuizReadyRef.current(response.sessionId);
          } else {
            navigate(`/quiz/${response.sessionId}`, { replace: true });
          }
        }
      } catch (error) {
        // Metered free tier (flagged): the daily cap is an expected user-flow
        // event — show the paywall, skip the failure toast/analytics (the
        // paywall itself fires free_cap_hit). Flag OFF → falls through to
        // today's generic error handling untouched.
        if (isFreeTierEnabled() && isDailyLimitError(error)) {
          logger.info('QuizLoading', 'Daily free-question cap reached', { limit: error.limit });
          setCapError(error);
          lastInitializationTime = 0;
          initializationStarted.current = false;
          return;
        }

        logger.error('QuizLoading', 'Failed to initialize quiz', { error });
        const errorMessage = error instanceof Error ? error.message : 'Failed to start quiz';
        setError(errorMessage);
        toast.error(errorMessage);
        
        // Track analytics for quiz initialization failures
        if (testType) {
          logAnalyticsEvent(ANALYTICS_EVENTS.QUIZ_START_ERROR, {
            test_type: testType,
            error: error instanceof Error ? error.message : 'Unknown error',
            error_type: error instanceof Error ? error.name : 'Unknown',
            categories: categories
          });
        }
        
        // Reset the flags if there's an error so user can try again
        lastInitializationTime = 0;
        initializationStarted.current = false;
      }
    };

    initializeQuiz();
    
    // Cleanup function
    return () => {
      // Reset the flags when component unmounts (but only if this component set them)
      initializationStarted.current = false;
    };
  }, [testType, sessionMode, targetQuestionCount, preferredDifficulty, categories, startQuestionId, urlSessionId, navigate, currentUser, isLoading]); // Remove unstable refs to prevent unnecessary re-initializations

  const getLoadingIcon = () => {
    if (categories && categories.length > 0) {
      return <Target className="w-12 h-12 text-primary-500" />;
    }
    return <Brain className="w-12 h-12 text-primary-500" />;
  };

  const getQuizTypeLabel = () => {
    if (categories && categories.length > 0) {
      return `${categories.join(', ')} Practice Quiz`;
    }
    if (testType) {
      const typeLabels = {
        [TestType.LCSW]: 'LCSW Practice Exam',
        [TestType.LMSW]: 'LMSW Practice Exam',
        [TestType.LBSW]: 'LBSW Practice Exam'
      };
      return typeLabels[testType] || 'Practice Quiz';
    }
    return 'Quiz';
  };

  if (capError) {
    return (
      <FreeCapReached
        limit={capError.limit}
        resetsAtUtc={capError.resetsAtUtc}
        testType={testType}
        context="initialize_session"
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-100 p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-neutral-100 rounded-xl shadow-ledge-lg p-8">
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-error-600" />
            </div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              Quiz Initialization Failed
            </h2>
            <p className="text-neutral-600 mb-6">
              {error}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                Return to Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 border border-neutral-300 text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-100 p-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-neutral-100 rounded-xl shadow-ledge-lg p-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            {getLoadingIcon()}
          </div>

          <h2 className="text-xl font-semibold text-neutral-900 mb-2">
            Preparing Your {getQuizTypeLabel()}
          </h2>

          <p className="text-neutral-600 mb-8">
            {currentStep}
          </p>

          <div className="flex items-center justify-center space-x-2 mb-6">
            <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
            <span className="text-sm text-neutral-500">
              This will only take a moment...
            </span>
          </div>

          {/* Progress indicator */}
          <div className="w-full bg-neutral-200 rounded-full h-1.5">
            <div className="bg-primary-500 h-1.5 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>

          {categories && categories.length > 0 && (
            <div className="mt-6 p-3 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary-800">
                <strong>Focus Areas:</strong> {categories.join(', ')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizLoading;